import {
  dass42Questions,
  dass42RatingScale,
  calculateDASS42Scores,
  getDASS42Severity,
} from '../../dass42Questions';
import { getAssessmentExamples } from '../../assessmentExamples';
import { ensureSession, fetchProgress, resumePoint, saveProgress, completeSession } from '../sessionApi';
import { guidanceOfficeFact, inTurn, NOTED, HEAVY } from './common';

const TOTAL = dass42Questions.length;

// Short everyday labels; the official DASS wording stays under each one on the button.
const SHORT = [
  { en: 'Not at all', fil: 'Hindi talaga' },
  { en: 'Some of the time', fil: 'Minsan' },
  { en: 'A good part of the time', fil: 'Madalas' },
  { en: 'Most of the time', fil: 'Halos palagi' },
];

// "Life wasn't worthwhile" (21) and "life was meaningless" (38)
const HOPELESSNESS_ITEMS = [21, 38];

/** Severe or extremely severe on any scale, or "a good part of the time" or more on a hopelessness item. */
export const dassIsWorrying = (answers) => {
  const severity = getDASS42Severity(calculateDASS42Scores(answers));
  if (Object.values(severity).some((level) => level === 'Severe' || level === 'Extremely Severe')) return true;
  return HOPELESSNESS_ITEMS.some((id) => {
    const index = dass42Questions.findIndex((q) => q.id === id);
    return index >= 0 && Number(answers[index]) >= 2;
  });
};

const dass42 = {
  key: 'dass42',
  total: TOTAL,
  feedbackCategory: 'dass42',
  followupReason: 'dass_followup',
  resultsTab: 'dass42',
  names: {
    title: { en: 'Mood and stress check', fil: 'Mood and stress check' },
    short: { en: 'Mood and stress', fil: 'Mood and stress' },
    code: 'DASS-42',
  },
  greeting: {
    en: "Hello! 👋 Let's start with the DASS-42 assessment to measure your stress, anxiety, and depression levels.",
    fil: 'Kumusta! 👋 Magsimula tayo sa DASS-42 assessment para masukat ang inyong stress, anxiety, at depression levels.',
  },
  facts: (lang, school) => [
    { icon: 'list', text: lang === 'fil' ? `${TOTAL} maiikling pahayag` : `${TOTAL} short statements` },
    { icon: 'calendar', text: lang === 'fil' ? 'Isipin ang nakaraang linggo' : 'Think about the past week' },
    guidanceOfficeFact(lang, school),
  ],
  layout: 'list',
  scale: dass42RatingScale.map((option, i) => ({
    value: option.value,
    label: SHORT[i],
    sub: { en: option.label, fil: option.labelFil },
  })),
  prompt: {
    en: 'Over the past week, how much did this apply to you?',
    fil: 'Sa nakaraang linggo, gaano ito naaangkop sa iyo?',
  },
  questionText: (i, lang) => {
    const q = dass42Questions[i];
    return (lang === 'fil' ? q.questionFil : q.question) || q.question;
  },
  examples: (i, lang) => {
    const q = dass42Questions[i];
    return (lang === 'fil' ? q.examplesFil : q.examples) || getAssessmentExamples('dass42', q.id, lang) || [];
  },
  echo: (value, lang) => `${value} · ${SHORT[value]?.[lang === 'fil' ? 'fil' : 'en'] || value}`,
  checkpoints: {
    10: { en: "That's 10. Thanks for staying with me, 32 more to go.", fil: '10 na. Salamat sa pagtiyaga, 32 pa.' },
    21: { en: "You're halfway. Take your time with the rest.", fil: 'Kalahati na tayo. Dahan-dahan lang sa natitira.' },
    32: { en: "Just 10 left. You're almost there.", fil: '10 na lang. Malapit na tayo.' },
  },
  isWorrying: dassIsWorrying,
  reply: (i, value, lang, n) =>
    Number(value) >= 2 ? { care: true, text: inTurn(HEAVY, lang, n) } : { text: inTurn(NOTED, lang, n) },

  load: async () => {
    const point = resumePoint(await fetchProgress('dass42'), TOTAL);
    const answers = Array.isArray(point.responses?.answers) ? point.responses.answers.slice(0, TOTAL) : [];
    return { sessionId: point.sessionId, nextIndex: point.nextIndex, answers };
  },
  save: async ({ sessionId, nextIndex, answers }) => {
    const id = await ensureSession('dass42', TOTAL, sessionId);
    await saveProgress({
      session_id: id,
      assessment_type: 'dass42',
      current_step: nextIndex,
      responses: { answers, scores: calculateDASS42Scores(answers) },
    });
    return id;
  },
  complete: async ({ sessionId, answers }) => {
    const id = await ensureSession('dass42', TOTAL, sessionId);
    const scores = calculateDASS42Scores(answers);
    const severity = getDASS42Severity(scores);
    await completeSession(id, {
      assessment_type: 'dass42',
      final_responses: { answers, scores },
      scores,
      interpretation: `Stress: ${severity.stress}, Anxiety: ${severity.anxiety}, Depression: ${severity.depression}`,
    });
    return id;
  },
};

export default dass42;
